import { useEffect, useState } from 'react';
import {
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from '@mui/material';

import Card from '@/components/Card';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import {
  ClientSession,
  Pagination,
  fetchFleetClientSessions,
} from '@/services/clientSessionsApi';

const PER_PAGE = 50;

const formatHash = (h: string) =>
  h === 'embedded' ? 'embedded' : h.slice(0, 8);

const formatDate = (dateStr: string) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ClientSessionsTable = () => {
  const [rows, setRows] = useState<ClientSession[] | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [platform, setPlatform] = useState<string>('');
  const [binary, setBinary] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setFetching(true);
    fetchFleetClientSessions({
      page,
      per_page: PER_PAGE,
      platform: platform || undefined,
      binary_version: binary || undefined,
    })
      .then((res) => {
        if (cancelled) return;
        setRows(res.data);
        setPagination(res.pagination);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg =
          e instanceof Error ? e.message : 'Failed to load client sessions';
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, platform, binary]);

  const handlePlatformChange = (e: SelectChangeEvent<string>) => {
    setPlatform(e.target.value);
    setPage(1);
  };

  const handleBinaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBinary(e.target.value);
    setPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  // Paginator expects a select-change handler. Per_page is fixed at 50 for
  // this view, so we no-op here but still satisfy the prop signature.
  const handleNumberItemsChange = (
    _event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    // intentional no-op: per_page is fixed at 50
  };

  return (
    <Card>
      <Card.Section>
        <Stack gap={16} style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Stack vertical gap={4}>
            <Small color="lighter">Platform</Small>
            <Select
              size="small"
              value={platform}
              onChange={handlePlatformChange}
              displayEmpty
              style={{ minWidth: 140 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ios">iOS</MenuItem>
              <MenuItem value="android">Android</MenuItem>
            </Select>
          </Stack>
          <Stack vertical gap={4}>
            <Small color="lighter">Binary version</Small>
            <TextField
              size="small"
              value={binary}
              onChange={handleBinaryChange}
              placeholder="e.g. 1.4.2"
            />
          </Stack>
        </Stack>
      </Card.Section>

      {error ? (
        <Card.Section>
          <Body color="lighter">{error}</Body>
        </Card.Section>
      ) : rows === null ? (
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '40px' }}>
            <CircularProgress />
          </Stack>
        </Card.Section>
      ) : rows.length === 0 ? (
        <Card.Section>
          <Body color="lighter">No client sessions found.</Body>
        </Card.Section>
      ) : (
        <Card.Section>
          <div style={{ position: 'relative', overflowX: 'auto' }}>
            {fetching && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                }}
              >
                <CircularProgress size={24} />
              </div>
            )}
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
              }}
            >
              <thead>
                <tr style={{ textAlign: 'left', color: '#6B7280' }}>
                  <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                    Worker
                  </th>
                  <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                    Platform
                  </th>
                  <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                    Binary
                  </th>
                  <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                    OTA hash
                  </th>
                  <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                    First seen
                  </th>
                  <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                    Last seen
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid #E5E7EB' }}>
                    <td style={{ padding: '8px 12px' }}>
                      <a
                        href={`/workers_v2/${r.worker_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1976d2', textDecoration: 'none' }}
                      >
                        <Body weight="semibold">#{r.worker_id}</Body>
                      </a>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Body>{r.platform}</Body>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Body>{r.binary_version}</Body>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Tooltip title={r.ota_hash}>
                        <span>
                          <Body>{formatHash(r.ota_hash)}</Body>
                        </span>
                      </Tooltip>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Small color="lighter">
                        {formatDate(r.first_seen_at)}
                      </Small>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Small color="lighter">
                        {formatDate(r.last_seen_at)}
                      </Small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card.Section>
      )}

      {pagination && rows && rows.length > 0 && (
        <Paginator
          currentPage={pagination.page}
          handleNumberItemsChange={handleNumberItemsChange}
          handlePageChange={handlePageChange}
          itemsLength={pagination.total}
          itemsPerPage={PER_PAGE}
          itemOptions={[50]}
        />
      )}
    </Card>
  );
};

export default ClientSessionsTable;
